import React, { useState, useRef, useEffect } from "react";
import { Camera, ArrowLeft, Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { IoIosUnlock } from "react-icons/io";
import { ContentLayout } from "../../components/admin-panel/content-layout";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import User from "../../assets/images/user.png";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import UserService from "../../services/user/user";
import { toastError, toastSuccess } from "../../components/ToastNotification";
import SuperAdminService from "../../services/superAdmin";
import { setUser } from "../../store/slices/superAdmin";
import { IoMdLock } from "react-icons/io";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import AdminService from "../../services/admin";
import { useNavigate } from "react-router";

const MyProfile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const user = useSelector((state) => state?.superAdmin?.user);
  const [image, setImage] = useState(User);
  const [profile, setProfile] = useState("");
  const [isImageUpdated, setIsImageUpdated] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    console.log("🚀  handleImageChange  file:", file);
    if (file) {
      setIsImageUpdated(true);
      setProfile(file);
      const reader = new FileReader();
      reader.onload = (e) => setImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current.click();
  };

  const handleSaveChanges = async (values) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("mobileNumber", values.number);
    if (isImageUpdated) {
      formData.append("profile", profile);
    }
    if (user?.admintype === "Admin" || user?.admintype === "OrganizationAdmin" || user?.admintype === "IndividualAdmin") {
      try {
        const response = await UserService?.updateAdminProfile(formData);
        if (response) {
          getUserDetails();
          toastSuccess(
            response?.data?.message || "Profile updated succeessfully!"
          );
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      }
    } else {
      try {
        const response = await SuperAdminService?.updateSuperAdminProfile(
          formData
        );
        if (response) {
          getUserDetails();
          toastSuccess(
            response?.data?.message || "Profile updated succeessfully!"
          );
        }
      } catch (error) {
        toastError(error?.response?.data?.message);
      }
    }
  };

  useEffect(() => {
    setImage(User); // Reset to the default image on component mount
  }, []);

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const formikPassword = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Old Password is required"),
      newPassword: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("New Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm Password is required"),
    }),
    onSubmit: async (values) => {
      const type = (user?.admintype === "Admin" || user?.admintype === "OrganizationAdmin" || user?.admintype === "IndividualAdmin") ? "Admin" : "SuperAdmin";
      try {
        const response = await AdminService?.changePassword(
          values?.oldPassword,
          values?.newPassword,
          type
        );
        if (response) {
          setIsDialogOpen(false);
          toastSuccess("Password changed successfully!");
          if (user?.admintype === "Admin" || user?.admintype === "OrganizationAdmin" || user?.admintype === "IndividualAdmin") {
            navigate("/user");
          } else {
            navigate("/admin");
          }
        }
      } catch (error) {
        setIsDialogOpen(true);
        toastError(error?.response?.data?.message);
      } finally {
        // formik.resetForm();
      }
    },
  });

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    formikPassword.resetForm(); // Reset the form state when dialog closes
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      number: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      number: Yup.string()
        .matches(/^\d+$/, "Must be a number")
        .required("Number is required"),
    }),
    onSubmit: handleSaveChanges,
  });

  useEffect(() => {
    formik.setFieldValue("name", user?.name);
    formik.setFieldValue("email", user?.email);
    formik.setFieldValue("number", user?.mobileNumber);
    if (user?.profile) {
      setImage(user?.profile);
    } else {
      setImage(User);
    }
  }, [user]);

  const getUserDetails = async () => {
    try {
      const response = await SuperAdminService?.getProfile();
      if (response) {
        dispatch(setUser(response?.data?.data));
      }
    } catch (error) {
      toastError(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    if (user) {
      getUserDetails();
    }
  }, []);

  return (
    <ContentLayout>
      <div>
        <div
          className="flex items-center gap-2 cursor-pointer mb-6 text-neutral-400 hover:text-white transition-all duration-300 w-fit group"
          onClick={() => navigate(-1)}
        >
          <div className="p-1.5 rounded-full border border-neutral-800 bg-neutral-900/50 group-hover:bg-neutral-800/80 group-hover:border-neutral-700 transition-all">
            <ArrowLeft className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium">Back</span>
        </div>
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>
              {(user?.admintype === "Admin" || user?.admintype === "OrganizationAdmin" || user?.admintype === "IndividualAdmin") ? "Admin" : "Super Admin"}
            </CardTitle>
            <CardDescription>
              <div className="flex justify-center">
                <div className="relative">
                  <div className="h-20 w-20 rounded-full overflow-hidden border">
                    <img
                      src={image}
                      alt="Admin Picture"
                      className="object-cover bg-center w-full h-full"
                    />
                  </div>
                  <div
                    className="cursor-pointer absolute bottom-0 right-0 bg-secondary border rounded-full p-1"
                    onClick={handleImageClick}
                  >
                    <Camera className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={formik.handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="User Name..."
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur} // Track field blur for validation
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="text-red-500">{formik.errors.name}</div>
                )}
              </div>
              <div className="space-y-2 pt-4">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  readOnly={true}
                  placeholder="Enter Your Email..."
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur} // Track field blur for validation
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-red-500">{formik.errors.email}</div>
                )}
              </div>
              <div className="space-y-2 pt-4 pb-6">
                <Label htmlFor="number">Number</Label>
                <Input
                  id="number"
                  name="number"
                  type="text" // Change to text to allow for potential leading zeros
                  placeholder="Enter Your Number..."
                  value={formik.values.number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur} // Track field blur for validation
                />
                {formik.touched.number && formik.errors.number && (
                  <div className="text-red-500">{formik.errors.number}</div>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/png, image/jpeg"
                onChange={handleImageChange}
              />
              <div className="flex tab:justify-between max-sm: max-sm:flex-col tab:items-center tab:gap-48 gap-4">
                <Button
                  type="submit"
                  // disabled={user?.admintype !== "Admin"}
                  className="bg-primary hover:bg-btnHover text-white"
                  disabled={formik.isSubmitting}
                > {formik.isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                  Save changes
                </Button>

                <div>
                  <Dialog
                    open={isDialogOpen}
                    onOpenChange={(open) =>
                      open ? setIsDialogOpen(true) : handleDialogClose()
                    }
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="flex justify-center items-center gap-1 w-full"
                      >
                        <IoMdLock size={18} />
                        <div>Change Password</div>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-xl max-sm:w-[calc(100vw-10vw)]">
                      <DialogHeader>
                        <DialogTitle>Change Password</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={formikPassword.handleSubmit}>
                        <div className="mt-5">
                          {/* Old Password */}
                          <div className="flex flex-col gap-2">
                            <Label
                              htmlFor="oldPassword"
                              className="whitespace-nowrap"
                            >
                              Old Password
                            </Label>
                            <div className="relative">
                              <Input
                                id="oldPassword"
                                name="oldPassword"
                                type={
                                  passwordVisibility.oldPassword
                                    ? "text"
                                    : "password"
                                }
                                value={formikPassword.values.oldPassword}
                                onChange={formikPassword.handleChange}
                                onBlur={formikPassword.handleBlur}
                              />
                              <button
                                type="button"
                                className={`absolute right-2 ${formikPassword.errors.oldPassword
                                  ? "top-[20px]"
                                  : "top-1/2"
                                  } transform -translate-y-1/2 text-gray-600`}
                                onClick={() =>
                                  togglePasswordVisibility("oldPassword")
                                }
                              >
                                {passwordVisibility.oldPassword ? (
                                  <AiFillEye />
                                ) : (
                                  <AiFillEyeInvisible />
                                )}
                              </button>
                              {formikPassword.touched.oldPassword &&
                                formikPassword.errors.oldPassword && (
                                  <div className="text-red-500 text-sm">
                                    {formikPassword.errors.oldPassword}
                                  </div>
                                )}
                            </div>
                          </div>

                          {/* New Password */}
                          <div className="flex flex-col gap-2 mt-5">
                            <Label
                              htmlFor="newPassword"
                              className="whitespace-nowrap"
                            >
                              New Password
                            </Label>
                            <div className="relative">
                              <Input
                                id="newPassword"
                                name="newPassword"
                                type={
                                  passwordVisibility.newPassword
                                    ? "text"
                                    : "password"
                                }
                                value={formikPassword.values.newPassword}
                                onChange={formikPassword.handleChange}
                                onBlur={formikPassword.handleBlur}
                              />
                              <button
                                type="button"
                                className={`absolute right-2 ${formikPassword.errors.newPassword
                                  ? "top-[20px]"
                                  : "top-1/2"
                                  } transform -translate-y-1/2 text-gray-600`}
                                onClick={() =>
                                  togglePasswordVisibility("newPassword")
                                }
                              >
                                {passwordVisibility.newPassword ? (
                                  <AiFillEye />
                                ) : (
                                  <AiFillEyeInvisible />
                                )}
                              </button>
                              {formikPassword.touched.newPassword &&
                                formikPassword.errors.newPassword && (
                                  <div className="text-red-500 text-sm">
                                    {formikPassword.errors.newPassword}
                                  </div>
                                )}
                            </div>
                          </div>

                          {/* Confirm Password */}
                          <div className="flex flex-col gap-2 mt-5">
                            <Label
                              htmlFor="confirmPassword"
                              className="whitespace-nowrap"
                            >
                              Confirm Password
                            </Label>
                            <div className="relative">
                              <Input
                                id="confirmPassword"
                                name="confirmPassword"
                                type={
                                  passwordVisibility.confirmPassword
                                    ? "text"
                                    : "password"
                                }
                                value={formikPassword.values.confirmPassword}
                                onChange={formikPassword.handleChange}
                                onBlur={formikPassword.handleBlur}
                              />
                              <button
                                type="button"
                                className={`absolute right-2 ${formikPassword.errors.confirmPassword
                                  ? "top-[20px]"
                                  : "top-1/2"
                                  } transform -translate-y-1/2 text-gray-600`}
                                onClick={() =>
                                  togglePasswordVisibility("confirmPassword")
                                }
                              >
                                {passwordVisibility.confirmPassword ? (
                                  <AiFillEye />
                                ) : (
                                  <AiFillEyeInvisible />
                                )}
                              </button>
                              {formikPassword.touched.confirmPassword &&
                                formikPassword.errors.confirmPassword && (
                                  <div className="text-red-500 text-sm">
                                    {formikPassword.errors.confirmPassword}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button type="submit" className="mt-5">
                            Save changes
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </ContentLayout>
  );
};

export default MyProfile;
